import * as i0 from '@angular/core';
import { InjectionToken, SecurityContext, ChangeDetectorRef, inject, Renderer2, EnvironmentInjector, ApplicationRef, ComponentRef, createComponent, ViewContainerRef, Component, ChangeDetectionStrategy, HostBinding, ViewChild, Input, HostListener } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ComponentFactoryService, HTML_PARSER_SERVICE_TOKEN, RouterOrExternalLinkDirective } from '@knorr-bremse-portals/ngx-components';

const HTML_ATTRIBUTE_ALLOWLIST_TOKEN = new InjectionToken('kb:html-component:attribute-allowlist');
const DEFAULT_ATTRIBUTE_ALLOWLIST = {
    href: SecurityContext.URL,
    target: SecurityContext.NONE,
    style: SecurityContext.STYLE,
    'kb-heading': SecurityContext.NONE,
    'direction': SecurityContext.NONE,
    'kb-link': SecurityContext.NONE,
    'kb-list': SecurityContext.NONE,
    'kb-paragraph': SecurityContext.NONE,
};
function provideHtmlAttributeAllowlist(allowlist) {
    return {
        provide: HTML_ATTRIBUTE_ALLOWLIST_TOKEN, useValue: allowlist,
    };
}
function provideDefaultHtmlAttributeAllowlist() {
    return provideHtmlAttributeAllowlist(DEFAULT_ATTRIBUTE_ALLOWLIST);
}
// We use this guard function instead of instanceof Element to support SSR
function isElementNode(node) {
    return node.nodeType === Node.ELEMENT_NODE;
}
class HtmlComponent {
    constructor() {
        this.cdr = inject(ChangeDetectorRef);
        this.renderer = inject(Renderer2);
        this.componentFactoryService = inject(ComponentFactoryService);
        this.router = inject(Router);
        this.domSanitizer = inject(DomSanitizer);
        this.environmentInjector = inject(EnvironmentInjector);
        this.htmlParserService = inject(HTML_PARSER_SERVICE_TOKEN);
        this.globalAttributeAllowlist = inject(HTML_ATTRIBUTE_ALLOWLIST_TOKEN);
        this.applicationRef = inject(ApplicationRef);
        // with angular v15 use directive composition API to directly create kbRouterOrExternalLink directive, or other directives
        this.kbRouterOrExternalLink = new RouterOrExternalLinkDirective(this.router, this.domSanitizer);
        this.createdComponents = [];
    }
    // https://angular.io/guide/security#sanitization-and-security-contexts
    // SecurityContext.NONE actually means any value is allowed
    set attributeAllowlist(allowlist) {
        this._attributeAllowlist = allowlist;
    }
    get attributeAllowlist() {
        return { ...this.globalAttributeAllowlist, ...this._attributeAllowlist };
    }
    ngOnChanges() {
        this.renderDom();
    }
    ngOnDestroy() {
        this.destroyCreatedComponents();
    }
    onClick(button, ctrlKey, metaKey, shiftKey, targetElement) {
        return this.kbRouterOrExternalLink.onClick(button, ctrlKey, metaKey, shiftKey, targetElement);
    }
    async renderDom() {
        const parsedDom = this.htmlParserService.parseFromString(this.html);
        const nodes = parsedDom.body.childNodes;
        this.clearHost();
        const hostElement = this.containerViewContainerRef.element.nativeElement;
        const childNodes = await this.createComponentTree(nodes);
        for (const childNode of childNodes) {
            this.renderer.appendChild(hostElement, childNode instanceof ComponentRef ?
                childNode.location.nativeElement :
                childNode);
        }
        this.cdr.markForCheck();
    }
    clearHost() {
        const hostElement = this.containerViewContainerRef.element.nativeElement;
        this.containerViewContainerRef.clear(); // We need this, else the components ngOnDestroy is not called
        this.destroyCreatedComponents();
        for (const childNode of hostElement.childNodes) {
            // A simple childNode.remove() somehow does not properly remove the item, after a change it would reoccure
            this.renderer.removeChild(hostElement, childNode);
        }
    }
    destroyCreatedComponents() {
        this.createdComponents.every(c => c.destroy());
        this.createdComponents = [];
    }
    async createComponentTree(nodes) {
        const createdNodes = [];
        for (const node of [...nodes]) { // It's important to make the NodeList static
            let createdNode;
            if (isElementNode(node)) {
                let childNodes = [];
                if (node.hasChildNodes()) {
                    childNodes = await this.createComponentTree(node.childNodes);
                }
                const componentMirror = await this.componentFactoryService.getComponentMirrorByElement(node);
                let createdElement;
                if (componentMirror !== null) {
                    const createdComponent = createComponent(componentMirror.type, {
                        environmentInjector: this.environmentInjector,
                        elementInjector: this.containerViewContainerRef.injector,
                        projectableNodes: [childNodes],
                        hostElement: this.renderer.createElement(node.tagName),
                    });
                    createdElement = createdComponent.location.nativeElement;
                    this.forEachAttribute(node, (attribute) => {
                        let name = attribute.name.toLowerCase();
                        const isJsonAttributeValue = attribute.name.startsWith('[') && attribute.name.endsWith(']');
                        if (isJsonAttributeValue) {
                            name = name.substring(1, name.length - 1);
                        }
                        const input = componentMirror.inputs.find(({ propName }) => propName.toLowerCase() === name);
                        if (input !== undefined) {
                            let value;
                            if (isJsonAttributeValue) {
                                value = JSON.parse(attribute.value);
                            }
                            else {
                                value = attribute.value;
                            }
                            createdComponent.setInput(input.propName, value);
                            return;
                        }
                        const selector = componentMirror.selector;
                        if (selector.includes(`[${attribute.name}]`) || selector.includes(`[${attribute.name}=`)) {
                            return;
                        }
                        this.copySanitizedAttribute(attribute, createdElement);
                    });
                    this.applicationRef.attachView(createdComponent.hostView);
                    this.createdComponents.push(createdComponent);
                }
                else {
                    // clone the node except for attributes to be able to later add them with a allowlist and sanitized
                    createdElement = this.renderer.createElement(node.tagName);
                    for (const childNode of childNodes) {
                        this.renderer.appendChild(createdElement, childNode);
                    }
                    this.forEachAttribute(node, (attribute) => {
                        this.copySanitizedAttribute(attribute, createdElement);
                    });
                }
                createdNode = createdElement;
            }
            else {
                createdNode = this.renderer.createText(node.textContent || '');
            }
            createdNodes.push(createdNode);
        }
        return createdNodes;
    }
    forEachAttribute(element, func) {
        for (let i = 0; i < element.attributes.length; i++) {
            const attribute = element.attributes[i];
            func(attribute);
        }
    }
    copySanitizedAttribute(attribute, targetElement) {
        // We need to allow _nghost attributes for server side generation component styles
        if (!this.isAngularAttribute(attribute) && this.attributeAllowlist[attribute.name] === undefined) {
            // component attribute selectors would trigger this warning
            console.warn(`HtmlComponent: Tried to set attribute ${attribute.name}="${attribute.value}", which is not in the allowlist`);
            return;
        }
        const sanitizedAttributeValue = this.attributeAllowlist[attribute.name] !== undefined ? this.domSanitizer.sanitize(this.attributeAllowlist[attribute.name], attribute.value) : '';
        if (sanitizedAttributeValue === null) {
            return;
        }
        targetElement.setAttribute(attribute.name, sanitizedAttributeValue);
    }
    isAngularAttribute(attribute) {
        return attribute.name.startsWith('_nghost-') && attribute.value === '' || attribute.name === 'ng-version';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HtmlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: HtmlComponent, isStandalone: true, selector: "[kb-html],kb-html", inputs: { html: "html", attributeAllowlist: "attributeAllowlist" }, host: { listeners: { "click": "onClick($event.button,$event.ctrlKey,$event.metaKey,$event.shiftKey,$event.target)" }, properties: { "attr.kbRouterOrExternalLink": "this.kbRouterOrExternalLink" } }, viewQueries: [{ propertyName: "containerViewContainerRef", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: '<div class="contents" #container></div>', isInline: true, styles: [".contents{display:contents}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HtmlComponent, decorators: [{
            type: Component,
            args: [{ selector: '[kb-html],kb-html', template: '<div class="contents" #container></div>', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [], styles: [".contents{display:contents}\n"] }]
        }], propDecorators: { kbRouterOrExternalLink: [{
                type: HostBinding,
                args: ['attr.kbRouterOrExternalLink']
            }], containerViewContainerRef: [{
                type: ViewChild,
                args: ['container', { static: true, read: ViewContainerRef }]
            }], html: [{
                type: Input,
                args: [{ required: true }]
            }], attributeAllowlist: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event.button', '$event.ctrlKey', '$event.metaKey', '$event.shiftKey', '$event.target']]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_ATTRIBUTE_ALLOWLIST, HtmlComponent, provideDefaultHtmlAttributeAllowlist, provideHtmlAttributeAllowlist };
